import React, { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from './Toast';
import { ApiService } from '../utils/api';
import { formatDateTime } from '../utils/datetime';

const BASE_PLACEHOLDERS = ['name', 'userId', 'eventName', 'eventDate', 'eventLocation'];

const fieldPlaceholders = (fields = []) => fields.flatMap((field) => (field ? [
  ...(field.type === 'file' || !field.label ? [] : [field.label.trim().toLowerCase()]),
  ...(field.optionsWithPricing || []).flatMap((option) => fieldPlaceholders(option?.subFields || [])),
] : []));

const hasFilters = (query = {}) => !!(query.searchTerm
  || query.startDate
  || Object.values(query.filters || {}).some((value) => value !== '' && value != null && !(Array.isArray(value) && !value.length)));

const JobRow = ({ job, onCancel }) => {
  const done = job.sent + job.failed;
  const percent = job.total ? Math.round((done / job.total) * 100) : 0;
  return (
    <li className="py-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate font-medium">{job.subject}</span>
        <span className="shrink-0 text-xs text-gray-500">{formatDateTime(job.createdAt)}</span>
      </div>
      <div className="mt-1 h-1.5 rounded bg-gray-200 overflow-hidden">
        <div className={`h-full ${job.failed ? 'bg-amber-500' : 'bg-blue-600'}`} style={{ width: `${percent}%` }} />
      </div>
      <div className="mt-1 flex items-center justify-between text-xs text-gray-600">
        <span>
          {job.sent}/{job.total} sent{job.failed ? ` · ${job.failed} failed` : ''} · {job.status.toLowerCase()}
        </span>
        {job.status === 'RUNNING' && (
          <button type="button" className="text-red-600 hover:underline" onClick={() => onCancel(job.id)}>Stop</button>
        )}
      </div>
      {job.failures?.length > 0 && (
        <details className="mt-1 text-xs text-gray-500">
          <summary className="cursor-pointer">Failures</summary>
          <ul className="mt-1 space-y-0.5">{job.failures.map((f) => <li key={f} className="break-all">{f}</li>)}</ul>
        </details>
      )}
    </li>
  );
};

const BulkEmailDialog = ({ open, onClose, eventId, filter, formSchema = [] }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const quillRef = useRef(null);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const placeholders = useMemo(
    () => [...new Set([...BASE_PLACEHOLDERS, ...fieldPlaceholders(formSchema)])],
    [formSchema],
  );

  const { data: jobs = [] } = useQuery({
    queryKey: ['bulkEmailJobs', eventId],
    queryFn: () => ApiService.getBulkEmailJobs(eventId),
    enabled: open && !!eventId,
    refetchInterval: (query) => ((query.state.data || []).some((job) => job.status === 'RUNNING') ? 3000 : false),
  });

  const insertPlaceholder = (name) => {
    const editor = quillRef.current?.getEditor();
    const token = `\${${name}}`;
    if (!editor) {
      setBody((prev) => `${prev}${token}`);
      return;
    }
    const range = editor.getSelection(true);
    editor.insertText(range ? range.index : editor.getLength() - 1, token, 'user');
  };

  const payload = () => ({ filter: { ...filter, page: undefined, size: undefined }, subject, body });

  const runPreview = async () => {
    setBusy(true);
    try {
      setPreview(await ApiService.previewBulkEmail(eventId, payload()));
    } catch (e) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const job = await ApiService.sendBulkEmail(eventId, payload());
      showSuccess(`Sending to ${job.total} people. You can close this window.`);
      setConfirming(false);
      setPreview(null);
      queryClient.invalidateQueries({ queryKey: ['bulkEmailJobs', eventId] });
    } catch (e) {
      showError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancelJob = async (jobId) => {
    try {
      await ApiService.cancelBulkEmailJob(eventId, jobId);
      queryClient.invalidateQueries({ queryKey: ['bulkEmailJobs', eventId] });
    } catch (e) {
      showError(e.message);
    }
  };

  const bodyEmpty = !body.replace(/<[^>]*>/g, '').trim();
  const running = jobs.some((job) => job.status === 'RUNNING');

  return (
    <Dialog open={open} onOpenChange={() => !busy && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Email attendees</DialogTitle>
          <DialogDescription>
            {hasFilters(filter)
              ? 'Goes to the registrations matching the table\'s current filters and search.'
              : 'No filters are applied, so this goes to every registration in the event.'}
            {' '}Each address gets one email.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Input placeholder="Subject" value={subject} maxLength={200}
            onChange={(e) => { setSubject(e.target.value); setPreview(null); }} />
          <div className="bg-white">
            <ReactQuill ref={quillRef} theme="snow" value={body} onChange={(value) => { setBody(value); setPreview(null); }} />
          </div>
          <div className="flex flex-wrap gap-1 items-center">
            <span className="text-xs text-gray-500 mr-1">Insert:</span>
            {placeholders.map((name) => (
              <button key={name} type="button" onClick={() => insertPlaceholder(name)}
                className="rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100">
                {name}
              </button>
            ))}
          </div>
          {preview && (
            <div className="rounded-lg border border-gray-200">
              <div className="border-b bg-gray-50 px-3 py-2 text-sm">
                <div><b>{preview.recipients}</b> recipient{preview.recipients === 1 ? '' : 's'}
                  {preview.withoutEmail ? ` · ${preview.withoutEmail} registration(s) have no email and are skipped` : ''}</div>
                {preview.sampleTo && <div className="text-gray-600">Sample for {preview.sampleTo}: <b>{preview.sampleSubject}</b></div>}
              </div>
              <iframe title="Email preview" sandbox="" srcDoc={preview.sampleHtml} className="w-full h-64 bg-white" />
            </div>
          )}
          {jobs.length > 0 && (
            <div>
              <div className="text-sm font-medium text-gray-700">Recent sends</div>
              <ul className="divide-y divide-gray-100">
                {jobs.map((job) => <JobRow key={job.id} job={job} onCancel={cancelJob} />)}
              </ul>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
          {!confirming ? (
            <>
              <Button variant="outline" onClick={runPreview} disabled={busy || !subject.trim() || bodyEmpty}>Preview</Button>
              <Button onClick={() => setConfirming(true)}
                disabled={busy || running || !preview || !preview.recipients}>
                Send{preview ? ` to ${preview.recipients}` : ''}
              </Button>
            </>
          ) : (
            <>
              <span className="text-sm text-gray-700 self-center">Send to {preview?.recipients} people?</span>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>No</Button>
              <Button onClick={send} disabled={busy}>Yes, send</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkEmailDialog;
